import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import styled, { css } from 'styled-components'

interface LoudSpeakerProps {
  children?: string
  labelColor?: string
}

interface StyledLoudSpeakerTextTrackProps {
  animationDuration: number
}

const StyledLoudSpeaker = styled.div<Pick<LoudSpeakerProps, 'labelColor'>>`
  ${({
    theme: {
      colors: { staticWhite, fillBrand },
      typography: {
        catchword: {
          medium2: { bundledCSS: medium2CatchwordCSS },
        },
      },
    },
    labelColor,
  }) => css`
    color: ${staticWhite};
    ${medium2CatchwordCSS};
    text-transform: uppercase;
    background-color: ${labelColor ?? fillBrand};
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    z-index: 1;
  `}
`

const StyledLoudSpeakerTextTrack = styled.div<StyledLoudSpeakerTextTrackProps>`
  ${({ animationDuration }) => css`
    display: flex;
    align-items: center;
    white-space: pre-wrap;
    width: max-content;
    height: 32px;
    animation: marquee ${animationDuration || 10}s linear infinite;
    @keyframes marquee {
      to {
        transform: translateX(-50%);
      }
    }
  `}
`

const StyledLoudSpeakerText = styled.span``

const LoudSpeaker: FunctionComponent<LoudSpeakerProps> = ({
  labelColor,
  children,
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [animationDuration, setAnimationDuration] = useState<number>(0)
  const preparedText: Array<string> = Array(20).fill(`${children} +++ `)

  const calculateSpeed = useCallback(() => {
    const trackWidth = trackRef.current?.clientWidth || 0
    const duration = trackWidth / 50

    setAnimationDuration(duration)
  }, [])

  useEffect(() => {
    calculateSpeed()
  }, [calculateSpeed, children])

  return (
    <StyledLoudSpeaker labelColor={labelColor}>
      <StyledLoudSpeakerTextTrack
        animationDuration={animationDuration}
        ref={trackRef}
      >
        {preparedText.map((text, index) => (
          <StyledLoudSpeakerText key={index} aria-hidden="true">
            {text}
          </StyledLoudSpeakerText>
        ))}
      </StyledLoudSpeakerTextTrack>
    </StyledLoudSpeaker>
  )
}

export default LoudSpeaker
